// Copyright (c) 2010-2025 IQGeo Group Plc. Use subject to conditions at $MYWORLD_HOME/Docs/legal.txt
import myw from './core';
import { TimeoutError } from './errors';
import { msg } from './localisation';
import { result } from 'underscore';
export { timer } from './timer';
export * from './redoStack';
export { convertMultiLangString } from './convertMultiLangString';
export * as keyboardEvent from './keyboardEvent';
import { DMS } from './dms';

/**
 * Module with a set of generic utility functions and classes used internally in myWorld
 * @exports util
 * @example
 * util.loadStyleSheet('mystyles.css')
 */

/**
 * Returns a promise that will resolve after the given delay in miliseconds
 * @param {number}delayMs
 */
export function delay(delayMs) {
    return new Promise(resolve => {
        setTimeout(resolve, delayMs);
    });
}

/**
 * Creates a new promise that rejects with a TimeoutError if the given promise does not settle within the given timeout
 * @template T
 * @param {Promise<T>} promise
 * @param {number}timeoutMs
 * @returns {Promise<T | void>}
 */
export function timeout(promise, timeoutMs) {
    let timeoutId;
    return Promise.race([
        promise,
        new Promise((resolve, reject) => {
            timeoutId = setTimeout(() => reject(new TimeoutError()), timeoutMs);
        })
    ]).finally(() => {
        clearTimeout(timeoutId);
    });
}

/**
 * Given an Iterable (an array, for example), or a promise of an Iterable, iterates serially over all the values in it,
 * executing the given iterator on each element.
 * If an element is a promise, the iterator will wait for it before proceeding.
 * @param {Promise<array>|array} promiseOrList
 * @param {function} func
 */
export async function each(promiseOrList, func) {
    const list = await promiseOrList;
    if (!list) return;
    const results = [];
    for (let el of list) {
        // eslint-disable-next-line no-await-in-loop
        results.push(await func(el));
    }
    return results;
}

/**
 * Returns a promise that results when the given function evaluates to truthy
 * @param {function} testFunc
 * @param {number}timeoutMs Timeout in ms where the returned promise returns anyway
 * @example
 * await th.until(() => !!detailsControl.editor); //wait for editor to catchup
 */
export async function until(testFunc, timeoutMs = 1000) {
    const start = Date.now();
    let timePassed = 0;
    let passed = false;
    do {
        passed = !!testFunc();
        if (!passed)
            // eslint-disable-next-line no-await-in-loop
            await new Promise(resolve => {
                setTimeout(resolve, 50);
            });
        timePassed = Date.now() - start;
    } while (!passed && timePassed < timeoutMs);
}

/**
 * Loads a css file. <br/>
 * Consider the alternative method of loading the file using requirejs and then loading into the dom using loadStyleRules()
 * @param  {string}         path            Url to the file
 * @param  {HTMLElement}     [styleElement]  Style element to add to the dom and check. Alternative argument to giving an url
 * @return {Promise}             Resolved when the style is confirmed to have loaded
 */
export function loadStyleSheet(path, styleElement) {
    //taken from: http://thudjs.tumblr.com/post/637855087/stylesheet-onload-or-lack-thereof
    //adapted to return a promise instead of calling a callback
    return new Promise((resolve, reject) => {
        const // reference to document.head for appending/ removing link nodes
            head = document.getElementsByTagName('head')[0]; // create the link node

        let styleEl;

        if (path) {
            styleEl = document.createElement('link');
            styleEl.setAttribute('href', path);
            styleEl.setAttribute('rel', 'stylesheet');
            styleEl.setAttribute('type', 'text/css');
        } else if (styleElement) {
            styleEl = styleElement;
        }

        let sheet, cssRules;
        // get the correct properties to check for depending on the browser
        if ('sheet' in styleEl) {
            sheet = 'sheet';
            cssRules = 'cssRules';
        } else {
            sheet = 'styleSheet';
            cssRules = 'rules';
        }

        const // how often to check if the stylesheet is loaded
            interval_id = setInterval(() => {
                // start checking whether the style sheet has successfully loaded
                try {
                    if (styleEl[sheet] && styleEl[sheet][cssRules].length) {
                        // SUCCESS! our style sheet has loaded
                        clearInterval(interval_id); // clear the counters
                        clearTimeout(timeout_id);
                        resolve(styleEl);
                    }
                } catch (e) {
                    //ignore error as we'll try again
                }
            }, 10),
            timeout_id = setTimeout(() => {
                // start counting down till fail
                clearInterval(interval_id); // clear the counters
                clearTimeout(timeout_id);
                head.removeChild(styleEl); // since the style sheet didn't load, remove the link node from the DOM
                reject(new Error('Stylesheet loading timed out'));
            }, 15000); // how long to wait before failing

        head.appendChild(styleEl); // insert the link node into the DOM and start loading the style sheet
    });
}

/**
 * Loads a string with css rules into the DOM
 * @param  {string|string[]} styleRules The css rules to load
 * @param  {string} [title]    Optional title for the set of rules
 * @return {Promise}
 */
export function loadStyleRules(styleRules, title) {
    // reference to document.head for appending/ removing link nodes
    const head = document.getElementsByTagName('head')[0];

    let styleRulesStr = '';

    if (typeof styleRules === 'string') {
        styleRulesStr = styleRules;
    } else {
        styleRulesStr = styleRules.join(' ');
    }

    //handle relative paths
    if (myw.baseUrl) styleRulesStr = styleRulesStr.replace('url("', 'url("' + myw.baseUrl);

    const styleEl = _createStyleElement(styleRulesStr, title);

    head.appendChild(styleEl);

    return Promise.resolve(styleEl);
}

/**
 * Loads an html file into a DOM element
 * @param  {string} url            Url to the html file
 * @param  {string} selector Selector that identifies an element of the DOM
 * @return {Promise}                Resolved when the file has loaded
 */
export async function loadInto(url, selector) {
    try {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(
                `Couldn't load ${JSON.stringify(url)}. Status: ${response.status} ${
                    response.statusText
                }`
            );
        }

        const html = await response.text();
        const element = document.querySelector(selector);
        if (element) {
            element.innerHTML = html;

            //if the html has script tags, we'll need to make them executable
            const scriptElements = element.querySelectorAll('script');
            //recursively replace all scripts with executable ones
            Array.from(scriptElements).forEach(scriptElement => {
                const clonedScriptElement = document.createElement('script');

                Array.from(scriptElement.attributes).forEach(attribute => {
                    clonedScriptElement.setAttribute(attribute.name, attribute.value);
                });

                clonedScriptElement.text = scriptElement.text;
                scriptElement.parentNode.replaceChild(clonedScriptElement, scriptElement);
            });
            return element;
        } else {
            throw new Error(`Element not found with the selector: ${selector}`);
        }
    } catch (error) {
        throw new Error("Couldn't load " + JSON.stringify(url) + '. Status: ' + error.message);
    }
}

export function readCookie(name) {
    const value = document.cookie.match('(^|;) ?' + name + '=([^;]*)(;|$)');
    return value ? decodeURIComponent(value[2]) : null;
}
export function writeCookie(name, value, timeout_hours) {
    const expires = new Date(),
        expires_milliseconds = 1000 * 60 * 60 * (timeout_hours || 0);
    expires.setTime(expires.getTime() + expires_milliseconds);
    let expiresStr = timeout_hours ? (expiresStr = ';expires=' + expires.toUTCString()) : '';
    document.cookie = name + '=' + encodeURIComponent(value) + ';path=/' + expiresStr;
}

/**
 * Returns the format of a (png, jpeg, or gif) image
 * @param  {string}     image64     Base64 encoded image
 * @return {string}                 'jpg', 'png', or 'gif'. Returns null if not a jpg, png, or gif image
 */
export function getImageFormatFor(image64) {
    const first4 = image64.substring(0, 4); //the first 4 characters in base64 correspond to the first 3 bytes in binary
    if ('/9gA' <= first4 && first4 <= '/9j/') return 'jpg';
    else if (first4 === 'iVBO') return 'png';
    else if (first4 === 'R0lG') return 'gif';
    else return null; //could not identify the image format
}

/*
 * Calculates and Returns the time since the date supplied as a translated string
 * @private
 */
export function timeSince(date) {
    const seconds = Math.floor((new Date() - date) / 1000);

    let interval = Math.floor(seconds / 31536000);

    if (interval > 1) {
        return msg('TimeSince', 'years_since', { count: interval });
    }
    interval = Math.floor(seconds / 2592000);
    if (interval > 1) {
        return msg('TimeSince', 'months_since', { count: interval });
    }
    interval = Math.floor(seconds / 86400);
    if (interval > 1) {
        return msg('TimeSince', 'days_since', { count: interval });
    }
    interval = Math.floor(seconds / 3600);
    if (interval > 1) {
        return msg('TimeSince', 'hours_since', { count: interval });
    }
    interval = Math.floor(seconds / 60);
    if (interval > 1) {
        return msg('TimeSince', 'minutes_since', { count: interval });
    }
    return msg('TimeSince', 'seconds_since', { count: Math.floor(seconds) });
}

/**
 * Converts the date to the Local date and time
 * @param  {string} rawDate        Date and time in UTC format
 * @param  {boolean}  withoutTime  If true, return date without the time
 * @return {string}                Local Date and time string in the local format
 *                                 dd month(short) yyyy, hh:mm (AM/PM)
 */
export function formatDate(rawDate, withoutTime) {
    const date = new Date(formatRawDate(rawDate));
    const dateString = date.toLocaleDateString(navigator.language, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
    const time = formatTime(date);
    if (withoutTime) return dateString;
    else return dateString + ', ' + time;
}

/*
 * Formats the UTC time so it can be accepted by the javascript Date object in IE8 as well
 * @param  {datetime} rawDate UTC datetime
 * @return {string}           UTC date and time string in 'yyyy/mm/dd hh:mm:ss UTC' format
 */
export function formatRawDate(rawDate) {
    //Replacing the 'T' used in the ISO format with space
    return rawDate.split('.')[0].replace(/-/g, '/').replace('T', ' ') + ' UTC';
}

/*
 * Extracts the time from the datetime string and formats it
 * @param  {string} date UTC date and time string
 * @return {string}      time string in 'hh:mm (AM/PM)' format
 */
export function formatTime(date) {
    const hh = date.getHours();
    let m = date.getMinutes();
    let dd = 'AM';
    let h = hh;
    if (h >= 12) {
        h = hh - 12;
        dd = 'PM';
    }
    if (h === 0) {
        h = 12;
    }
    m = m < 10 ? '0' + m : m;

    return h + ':' + m + ' ' + dd;
}

/**
 * Checks if a set of search terms partially match any of the terms on a second set
 * @param  {string} searchTerms  Terms to check if they partially at least one of the terms in the second set
 * @param  {string} idTerms      Terms to match against
 * @return {boolean|string}     False if the terms didn't match or the remaining terms if it did
 * @private
 */
export function termsMatch(searchTerms, idTerms) {
    if (!idTerms) return false;

    let matches = false;
    const remainingTerms = [];

    const matchesIdTerm = (searchTerm, idTerm) => idTerm.includes(searchTerm);

    idTerms = idTerms.toLowerCase().trim().split(' ');
    searchTerms = searchTerms.trim().split(' ');
    for (let i = 0; i < searchTerms.length; i++) {
        const searchTerm = searchTerms[i].toLowerCase();
        if (idTerms.some(t => matchesIdTerm(searchTerm, t))) {
            matches = true;
            //we don't want the idTerm that was matched to exclude any more search terms
            idTerms = idTerms.filter(t => !matchesIdTerm(searchTerm, t));
        } else {
            remainingTerms.push(searchTerms[i]); //return original terms (not lowercased)
        }
    }

    if (matches) return remainingTerms.join(' ');
    else return false;
}

/**
 * Returns the union of a list of arrays without exceding a given number of elements
 * If the total number of elements exceeds the given maximum, then elements similar
 * number of elements is taken from each arrays.
 * @param  {Array<Array>} arrays
 * @param  {number}maxElements
 * @return {Array}
 */
export function sampledUnion(arrays, maxElements) {
    const slicedArrays = [];
    let nElems = 0;
    let row = 0;
    const lengthSum = arrays.reduce((memo, arr) => memo + arr.length, 0);

    maxElements = Math.min(maxElements, lengthSum);

    //get one element out of each array until we have enough elements
    while (nElems < maxElements) {
        for (let arrayId = 0; arrayId < arrays.length; arrayId++) {
            const array = arrays[arrayId];
            if (row === 0) slicedArrays[arrayId] = [];

            if (nElems < maxElements && row < array.length) {
                nElems++;
                slicedArrays[arrayId].push(array[row]);
            }
        }
        row++;
    }
    return slicedArrays.flat();
}

/**
 * Safer alternative to eval() that will only evaluate global variables and property accessors
 * @param  {string} expr Expression to be evaluated. ex: 'MywVectorLayer'
 * @param  {object} [context] Object on which to begin looking for the first property name. Defaults to window/global
 * @param  {boolean} [executeFunctions]
 * @return {object | undefined}
 */
export function evalAccessors(expr, context, executeFunctions) {
    if (typeof expr !== 'string') return undefined;
    const parts = expr.split('.');
    const g = context || (typeof global !== 'undefined' ? global : window);

    return parts.reduce((current, part) => {
        if (executeFunctions) return current && result(current, part);
        else return current?.[part];
    }, g);
}

/**
 * Parses an option object converting expressions stored in string properties to proper values
 * Valid expressions are the ones that can be parsed with evalAccessors and are
 * identified by starting with 'lambda:'
 * @param  {object} jsonObj An object obtained from parsing json
 * @return {object}
 */
export function processOptionsFromJson(jsonObj) {
    Object.entries(jsonObj ?? {}).forEach(([key, item]) => {
        if (typeof item === 'string' && item.startsWith('lambda:')) {
            const expr = item.slice(7);
            jsonObj[key] = evalAccessors(expr);
        }
    });
    return jsonObj;
}

/**
 * Wraps methods with additional code.
 * Similar functionality to python decorators
 * @param  {object} context         Object for which we want the methods to become wrapped
 * @param  {function} wrapper       Function that executes the additional code
 * @param  {...string} methodNames   Name of method(s) to wrap
 */
export function wrapMethodsWith(context, wrapper, ...methodNames) {
    methodNames.forEach(methodName => {
        const method = context[methodName];
        if (typeof method === 'function') {
            context[methodName] = wrapper(method);
        }
    });
}

/**
 * Concatenates the results of several promises
 * Rejected promises are ignored except if all promises are rejected - in this case
 * the returned promise is rejected with the reason for the rejection of the first promise
 * @param {Array<any>} promises
 * @return {Promise<Array<any>>}
 */
export async function concatPromiseResults(promises) {
    const results = await Promise.allSettled(promises);
    const fulfilled = results.filter(result => result.status === 'fulfilled');

    if (promises.length > 0 && !fulfilled.length) throw results[0].reason;
    else return fulfilled.flatMap(result => result.value);
}

/**
 * Converts a given relative url to account for option baseUrl
 * If the given url is absolute it is returned as is
 * @return {url}
 */
export function convertUrl(url) {
    const regexp = /(http|https|data):\/\//;
    const match = url.match(regexp);
    if (match) return url;
    else return myw.baseUrl + url;
}

/**
 * Adjust a relative url in a given icon definition to account for myw.baseUrl
 * @return {object} iconDef
 */
export function fixIconDefPath(iconDef) {
    if (myw.baseUrl && iconDef.iconUrl)
        return Object.assign({}, iconDef, { iconUrl: convertUrl(iconDef.iconUrl) });
    else return iconDef;
}

/**
 * Get the url specified parameter with a given name
 * @param  {string} name name of the parameter
 * @return {string}      Parameter value
 */
export function getUrlParam(name) {
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const params = new URLSearchParams(window.location.search);
    return params.get(name) ?? '';
}

/**
 * Copies a given value to the operating system's clipboard
 * @param  {string|number} data     data to copy
 */
export function copyToClipboard(data) {
    // we still need a empty string in case the data is null
    const text = data == null ? '' : String(data);
    // Prefer the modern Clipboard API if available
    if (window.isSecureContext && navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).catch(e => console.error(e));
        return;
    }

    // Fallback: use dummy input for legacy browsers
    const dummy = document.createElement('input');
    document.body.appendChild(dummy);
    dummy.value = text;
    dummy.setAttribute('readonly', '');
    dummy.select();
    try {
        document.execCommand('copy');
    } catch (e) {
        console.error(e);
    }
    document.body.removeChild(dummy);
}

/**
 * Writes the content of a document node to the clipboard
 * @param {string} selector jQuery selector for the target node'
 */
export function copyNodeToClipboard(selector) {
    //ENH: Merge common functionality with copyToClipboard
    //ENH: consider navigator.clipboard.writeText()
    let element = document.querySelector(selector);
    if (!element) return console.error('Element not found with the selector:', selector);
    let range = document.createRange();
    range.selectNode(element);
    window.getSelection().removeAllRanges();
    window.getSelection().addRange(range);

    try {
        document.execCommand('copy');
    } catch (error) {
        console.error('Copy to clipboard failed:', error);
    }

    window.getSelection().removeAllRanges();
}

/**
 * Return latLng as a neatly formatted string
 * @param {{ lat: number; lng: number }} latlng
 * @param {number} [n_dp]
 * @param {'d' | 'dm' | 'dms'} [dmsFormat='d'] `'dm'` includes minutes, `'dms'` includes minutes and seconds.
 */
export function formatLatLng(latlng, n_dp, dmsFormat = 'd') {
    let lat_ch, lng_ch, lat_str, lng_str;

    // Determine suffices
    lat_ch = latlng.lat < 0 ? 'S' : 'N';
    lng_ch = latlng.lng < 0 ? 'W' : 'E';

    // Build string
    if (dmsFormat === 'd') {
        if (typeof n_dp === 'number') {
            lat_str = Number(Math.abs(latlng.lat).toFixed(n_dp));
            lng_str = Number(Math.abs(latlng.lng).toFixed(n_dp));
        } else {
            lat_str = Math.abs(latlng.lat).toString();
            lng_str = Math.abs(latlng.lng).toString();
        }
    } else {
        const dmsLat = DMS.fromDecimalDegrees(latlng.lat);
        const dmsLng = DMS.fromDecimalDegrees(latlng.lng);

        lat_str = dmsLat.format({ dmsFormat, decimalPlaces: n_dp, abs: true });
        lng_str = dmsLng.format({ dmsFormat, decimalPlaces: n_dp, abs: true });
    }

    return lat_str + ' ' + lat_ch + ', ' + lng_str + ' ' + lng_ch;
}

// TBR: `parseLatLng` is no longer used internally
/**
 * Parses a string to identify a lat/lng coordinate.
 * Returns `undefined` if it couldn't find a match.
 * @param {string} str
 * @return {{ lat: number; lng: number } | void} Object with properties `lat` and `lng`
 */
export function parseLatLng(str) {
    let lat, lng, match;

    // Try unqualified lat,lng pair e.g. 1.234,34.12
    match = matchUnqualifiedCoord(str);
    if (match) {
        return { lat: match[0], lng: match[1] };
    }

    // Try qualified lat,lng pair e.g. 1.234N,34.12E
    match = /^\s*(\d+)(\.\d+|)\s*(N|S)\s*,\s*(\d+)(\.\d+|)\s*(E|W)\s*$/i.exec(str);
    if (match) {
        lat = Number(match[1] + match[2]);
        lng = Number(match[4] + match[5]);
        if (match[3].toUpperCase() === 'S') lat *= -1;
        if (match[6].toUpperCase() === 'W') lng *= -1;
        return { lat, lng };
    }

    // Try qualified lng,lat pair e.g. 1.234E,34.12N
    match = /^\s*(\d+)(\.\d+|)\s*(E|W)\s*,\s*(\d+)(\.\d+|)\s*(N|S)\s*$/i.exec(str);
    if (match) {
        lng = Number(match[1] + match[2]);
        lat = Number(match[4] + match[5]);
        if (match[3].toUpperCase() === 'W') lng *= -1;
        if (match[6].toUpperCase() === 'S') lat *= -1;
        return { lat, lng };
    }
}

/**
 * Matches unqualified x/y pairs e.g. `1.234,34.12`.
 * Is whitespace tolerant and allows for optional leading `+` or `-` signs.
 * Returns `undefined` if it couldn't find a match.
 * @param {string} str
 * @return {[number, number] | void}
 */
export function matchUnqualifiedCoord(str) {
    // Count the commas
    if (str.match(/,/g)?.length !== 1) return;

    // Convert commas separated list to Number array (length 2)
    const unqualifiedCoords = str.split(',').map(Number);
    if (unqualifiedCoords.includes(NaN)) return;

    return /** @type {[number, number]} */ (unqualifiedCoords);
}

/**
 * Checks if lat/lng coord is within valid bounds, i.e. lat is within -90 to 90 degrees and
 * lng is within -180 to 180 degrees.
 * @param {{ lat: number; lng: number }} coord
 * @returns {boolean}
 */
export function isLatLngWithinBounds(coord) {
    const { lat, lng } = coord;

    return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

/**
 * Converts a base64 string into a UInt8Array
 * @param {string} b64Data The data as a base64 string
 * @returns {ArrayBuffer} The data as an ArrayBuffer
 */
export function b64toArrayBuffer(b64Data) {
    const binaryString = window.atob(b64Data);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
}

/**
 *  Converts a base64 string into a Blob
 *  @Returns Blob
 */
export function b64toBlob(b64Data, contentType, sliceSize) {
    contentType = contentType || '';
    sliceSize = sliceSize || 512;

    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);

        byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
}

/**
 * Converts a blob to base64 data
 * @param {Blob} blob
 * @param {object} [options]
 * @param {boolean} [options.includeMime] If true, the returned base64 string will include the mime type as a prefix (e.g. `data:image/png;base64,`)
 * @returns {Promise<string>} The data as a base64 string
 */
export function blobToB64(blob, options = {}) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const dataUrl = reader.result;
            if (options.includeMime) {
                resolve(dataUrl);
            } else {
                const base64Index = dataUrl.indexOf(',') + 1;
                resolve(dataUrl.substring(base64Index));
            }
        };
        reader.onerror = error => reject(error);
        reader.readAsDataURL(blob);
    });
}

/**
 * Converts decimal degrees to radians
 * @param {number}   degrees angle in decimal degrees
 * @returns {number} angle in radians
 */
export function toRadians(degrees) {
    return (degrees * Math.PI) / 180;
}

/**
 * Converts  to radians decimal degrees
 * @param {number} radians angle in radians
 * @returns {number} angle in decimal degrees
 */
export function toDegrees(radians) {
    return (radians * 180) / Math.PI;
}

/**
 * Distance between two angles considering negative values and values over one circunference (360 degrees or 2PI)
 * @param {number} angle1
 * @param {number} angle2
 * @param {number} [circunference=360] Defaults to 360 (degrees), Use Math.PI*2 for angles in radians
 * @returns {number}
 */
export function angleDistance(angle1, angle2, circunference = 360) {
    const distance = Math.abs(modulo(angle1, circunference) - modulo(angle2, circunference));
    return Math.min(distance, circunference - distance);
}

/**
 * Modulo operator
 * @param {number} value
 * @param {number} divisor
 * @returns {number}
 */
export function modulo(value, divisor) {
    return ((value % divisor) + divisor) % divisor;
}

/**
 * Create a new Point that is a set length and direction from
 * an initial point.
 * @param {Array} point    initial point (openlayers coordinates)
 * @param {number} length     length in pixels
 * @param {number} direction  direction in decimal degrees
 * @return {Array} new point a set distance/direction away from initial point (openlayers coordinates)
 */
export function createPointByVector(point, length, direction) {
    const theta = toRadians(direction);

    const dX = length * Math.sin(theta);
    const dY = length * Math.cos(theta);
    const newCoords = [point[0] + dX, point[1] + dY];
    return newCoords;
}

/**
 * Returns input string to Title case string (I'm a little teapot -> I'm A Little Teapot)
 * @param {string} string
 */
export function toTitleCase(string) {
    return string
        .toLowerCase()
        .split(' ')
        .map(word => word.replace(word[0], word[0].toUpperCase()))
        .join(' ');
}

/**
 * Joins a list of SQL strings into a single string, separated by `joinStr`.
 * @param {string} joinStr
 * @param {Array<SqlString>} sqlStrings
 * @returns {SqlString}
 */
export function joinSqlStrings(joinStr, sqlStrings) {
    /** @type {Record<string, unknown>} */
    const params = {};
    for (const sqlStr of sqlStrings) {
        if (!sqlStr.params) continue;
        //include params in result detecting clashes
        //sort parameter names so longer names get replaced first (if clashing)
        for (let [paramName, value] of Object.entries(sqlStr.params)) {
            const exists = Object.prototype.hasOwnProperty.call(params, paramName);
            if (exists && value !== params[paramName]) {
                throw new Error(`Bind param clash: ${paramName}`);
            }
            params[paramName] = value;
        }
    }
    if (!sqlStrings.length) return;

    return getSqlString(`( ${sqlStrings.join(` ${joinStr} `)} )`, params);
}

/**
 * Creates a new `String` object with an additional `params` property.
 * @param {string} sql
 * @param {Record<string, unknown>} params
 * @returns {SqlString}
 */
export function getSqlString(sql, params) {
    const sqlString = /** @type {SqlString} */ (new String(sql));

    sqlString.params = params;

    return sqlString;
}

export function isJson(str) {
    try {
        JSON.parse(str);
    } catch (e) {
        return false;
    }
    return true;
}

/*
 * As of 27/09/2019, iPadOS 13 doesn't fire context menu event. This seems to affect ol-contextmenu as well
 * This function applies hacks to get it working again if running iPadOS/iPhoneOS 13
 * Solution copied from https://github.com/Leaflet/Leaflet/issues/6817#issuecomment-535523415
 * ENH: find a better fix
 */
export function applyIOS13ContextMenuHack(el) {
    if (!myw.browser.appleTouchScreen) return;

    let timer = null;

    const fireLongPressEvent = originalEvent => {
        clearLongPressTimer();

        const el = originalEvent.target,
            x = originalEvent.touches[0].clientX,
            y = originalEvent.touches[0].clientY;

        // This will emulate contextmenu mouse event
        const event = new MouseEvent('contextmenu', {
            bubbles: true,
            cancelable: true,
            clientX: x,
            clientY: y
        });

        // fire the long-press event
        const suppressClickEvent = el.dispatchEvent.call(el, event);

        if (suppressClickEvent) {
            // temporarily intercept and clear the next click
            el.addEventListener(
                'touchend',
                function clearMouseUp(e) {
                    el.removeEventListener('touchend', clearMouseUp, true);
                    cancelEvent(e);
                },
                true
            );
        }
    };

    const startLongPressTimer = e => {
        clearLongPressTimer(e);
        timer = setTimeout(fireLongPressEvent.bind(null, e), 1000);
    };

    const clearLongPressTimer = () => {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
    };

    const cancelEvent = e => {
        e.stopImmediatePropagation();
        e.preventDefault();
        e.stopPropagation();
    };

    // hook events that clear a pending long press event
    el.addEventListener('touchcancel', clearLongPressTimer, true);
    el.addEventListener('touchend', clearLongPressTimer, true);
    el.addEventListener('touchmove', clearLongPressTimer, true);

    // hook events that can trigger a long press event
    el.addEventListener('touchstart', startLongPressTimer, true); // <- start
}

export function argsAsURI(args) {
    return new URLSearchParams(args).toString();
}

function _createStyleElement(rulesStr, title) {
    const styleEl = document.createElement('style');
    styleEl.type = 'text/css';

    if (title) styleEl.title = title;

    if (styleEl.styleSheet) styleEl.styleSheet.cssText = rulesStr;
    else styleEl.appendChild(document.createTextNode(rulesStr));

    return styleEl;
}

/**
 * Invokes a download from the given URL and filename
 * @param {string} url
 * @param {string} filename
 */
export function invokeDownload(url, filename) {
    const element = document.createElement('a');
    element.setAttribute('href', url);
    element.setAttribute('download', filename ? filename : url);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
}

/**
 * Generates a download from the given content and filename
 * @param {Blob|BlobPart} content
 * @param {string} filename
 */
export function generateDownload(content, filename) {
    if (!(content instanceof Blob)) {
        content = new Blob([content], { type: 'octet/stream' });
    }

    const url = window.URL.createObjectURL(content);
    try {
        invokeDownload(url, filename);
    } finally {
        window.URL.revokeObjectURL(url);
    }
}

/**
 * If a path segment can contain a `/`, it needs to be double encoded.
 * It then gets decoded once by Pyramid, and must be decoded again in the server controller.
 * @param {string} segment
 */
export function doubleEncode(segment) {
    return encodeURIComponent(encodeURIComponent(segment));
}

/**
 * Compares two strings for equality, ignoring case and leading/trailing whitespace.
 *
 * Returns true if both strings are null or undefined, or if their trimmed, lowercased values are equal.
 *
 * @param {string|null|undefined} str1 - The first string to compare.
 * @param {string|null|undefined} str2 - The second string to compare.
 * @returns {boolean} True if the strings are considered equal, false otherwise.
 */
export function areStringsEqual(str1, str2) {
    // Check if both are null or undefined.
    // eslint-disable-next-line eqeqeq
    if (str1 == null || str2 == null) return str1 == str2;

    // Compare the strings directly.
    return str1.trim().toLowerCase() === str2.trim().toLowerCase();
}

/**
 * Returns `str` with all regex special characters escaped.
 * Copied from https://stackoverflow.com/questions/3561493/is-there-a-regexp-escape-function-in-javascript/3561711#3561711
 * @param {string} str
 * @returns {string}
 */
export function escapeRegex(str) {
    return str.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
}

/**
 * Formats a string by replacing placeholders in `{key}` format with values from the `values` object.
 * @param {string} str
 * @param {Record<string, string>} values
 * @returns {string}
 * @example
 * replacePlaceholders('Hello, {name}!', { name: 'Alice' }) // returns 'Hello, Alice!'
 */
export function replacePlaceholders(str, values) {
    let formattedStr = str;

    Object.entries(values).forEach(([key, value]) => {
        formattedStr = formattedStr.replaceAll(`{${key}}`, value);
    });

    return formattedStr;
}

/**
 * Generate a timestamped filename
 * @param {string} basename - Base name for the file (e.g., 'building_export')
 * @param {string} extension - File extension (e.g., 'kml', 'kmz')
 * @return {string} Timestamped filename (e.g., 'building_export_Nov-25-2025_14-02-11.kml')
 */
export function generateTimestampedFilename(basename, extension) {
    const now = new Date();
    const month = now.toLocaleString('default', { month: 'short' });
    const day = String(now.getDate()).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const timestamp = `${month}-${day}-${year}_${hours}-${minutes}-${seconds}`;
    return `${basename}_${timestamp}.${extension}`;
}

/**
 * A `String` object with an additional `params` property.
 * @typedef {InstanceType<typeof String> & { params: Record<string, unknown> }} SqlString
 */

/**
 * Adds a data URI prefix to a base64-encoded data string if it does not already have one.
 *
 * @param {string} data - The base64-encoded string, optionally without the data URI prefix.
 * @param {string} imageType - The MIME type of the data (e.g., 'image/png', 'image/jpeg').
 * @returns {string} The base64 data string with the appropriate data URI prefix.
 */
export function addDataUriToBase64String(data, imageType) {
    if (data?.startsWith('data')) return data;
    return `data:${imageType};base64,${data}`;
}

/**
 * Adds a data URI prefix to a base64-encoded image string if it does not already have one.
 *
 * @param {string} imageData - The base64-encoded image string, optionally without the data URI prefix.
 * @returns {string} The base64 image string with the appropriate data URI prefix.
 * @throws {Error} Throws an error if the image format cannot be identified.
 */
export function addDataUriToImageData(imageData) {
    if (imageData?.startsWith('data')) return imageData;

    const imageType = getImageFormatFor(imageData);
    if (!imageType) throw new Error(msg('Application', 'invalid_image_format'));

    return `data:image/${imageType};base64,${imageData}`;
}

/**
 * Removes the data URI prefix from a base64-encoded image string if it has one.
 *
 * @param {string} data - The base64-encoded image string, optionally with the data URI prefix.
 * @returns {string} The base64 image string without the data URI prefix.
 * @throws {Error} Throws an error if the image data URI cannot be identified.
 */
export function removeDataUriFromBase64String(data) {
    if (!data?.startsWith('data')) return data;

    const commaIndex = data.indexOf(',');
    if (commaIndex === -1) {
        throw new Error(msg('Application', 'invalid_image_data_uri'));
    }

    return data.substring(commaIndex + 1);
}

/**
 * Compares two values and returns a number indicating their relative order.
 *
 * @param {*} aValue - The first value to compare. Can be of any type.
 * @param {*} bValue - The second value to compare. Can be of any type.
 * @returns {number}
 *   - Returns 0 if both values are equal (including both being null/undefined).
 *   - Returns a negative number if aValue is less than bValue.
 *   - Returns a positive number if aValue is greater than bValue.
 *
 * @description
 * This function handles null and undefined values, treating them as less than any other value.
 * If both values are strings, it uses localeCompare for comparison.
 * If both values are numbers, it performs a numerical comparison.
 * For other types, it returns 0.
 */
export function compareValues(aValue, bValue) {
    // Handle null/undefined values
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return -1;
    if (bValue == null) return 1;

    if (typeof aValue === 'string' && typeof bValue === 'string')
        return aValue.localeCompare(bValue);

    if (typeof aValue === 'number' && typeof bValue === 'number') return aValue - bValue;
    return 0;
}

/**
 * Formats a file size in KB into a human-readable string with appropriate units.
 * We currently have no data stored in bytes, so we start from KB.
 *
 * @param {number} value - The file size in KB.
 * @returns {string} The formatted file size string.
 */
export function formatFileSize(value, originalUnit = 'kb') {
    if (!value || value === 0) return '';

    const units = ['b', 'kb', 'mb', 'gb', 'tb'];
    let u = units.indexOf(originalUnit.toLowerCase());
    const s = 1024;

    while (value >= s) {
        value /= s;
        u++;
    }

    return msg('Application', `size_${units[u]}`, { value: value.toFixed(2) });
}
